// The command palette — V2's PRIMARY navigation.
//
// Classic has fifty routes, six identifier conventions (:deviceId,
// :thingName, :username, :unifiedId, :tenantId, :groupId) and no global
// search of any kind. For two expert users the daily motion is "get me to
// this thing", and today that means knowing which of eleven sections owns
// it. At 3,000 devices browsing stops being viable at all.
//
// So the tree becomes a map you consult occasionally, and this is how you
// move. Destinations match instantly from the local IA; entities are
// searched against the endpoints that already support a query — screens by
// plate/driver/thing name, and campaigns by name.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ALL_ITEMS } from './nav';
import api from '../../services/api';
import s from './CommandPalette.module.css';

const DEBOUNCE_MS = 180;

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [entities, setEntities] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => { if (open) { setQ(''); setEntities([]); setCursor(0); } }, [open]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Destinations: local, instant.
  const destinations = useMemo(() => {
    const n = q.trim().toLowerCase();
    const pool = ALL_ITEMS.map((i) => ({
      kind: 'Go', title: i.label, sub: i.why, to: i.path, id: i.id,
    }));
    if (!n) return pool.slice(0, 6);
    return pool.filter((d) => d.title.toLowerCase().includes(n) || d.to.includes(n)).slice(0, 6);
  }, [q]);

  // Entities: debounced, against endpoints that genuinely support search.
  useEffect(() => {
    const n = q.trim();
    if (n.length < 2) { setEntities([]); return; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      const found = [];
      try {
        const [screens, campaigns] = await Promise.allSettled([
          api.get('/media-screens', { params: { q: n, limit: 6 } }),
          api.get('/campaigns', { params: { q: n, limit: 6 } }),
        ]);
        if (screens.status === 'fulfilled') {
          for (const r of screens.value.data?.data?.rows || []) {
            found.push({
              kind: 'Screen', id: `s:${r.thingName}`,
              title: r.plate || r.placementName || r.thingName,
              sub: [r.driverName, r.city, r.thingName].filter(Boolean).join(' · '),
              to: `/ggos-devices/${r.thingName}`, external: true,
            });
          }
        }
        if (campaigns.status === 'fulfilled') {
          for (const c of campaigns.value.data?.data || []) {
            found.push({
              kind: 'Campaign', id: `c:${c.id}`,
              title: c.campaign_name,
              sub: [c.tenant_name, c.campaign_status, `${c.adsets ?? 0} adsets`].filter(Boolean).join(' · '),
              to: `/campaigns/${c.id}`, external: true,
            });
          }
        }
      } catch { /* a failed lookup must never break the palette */ }
      if (mine === seq.current) { setEntities(found); setSearching(false); }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const all = useMemo(() => [...destinations, ...entities], [destinations, entities]);
  useEffect(() => { setCursor((c) => Math.min(c, Math.max(0, all.length - 1))); }, [all.length]);

  const go = useCallback((item) => {
    if (!item) return;
    onClose();
    // Classic routes live outside the V2 router subtree, so a full navigation
    // is correct — and honest: you are leaving V2 for a screen it has not
    // ported yet.
    if (item.external) window.location.assign(item.to);
    else navigate(item.to);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % Math.max(1, all.length)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c - 1 + all.length) % Math.max(1, all.length)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(all[cursor]); }
  };

  if (!open) return null;

  let lastKind = null;
  return (
    <div className={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={s.panel} role="dialog" aria-modal="true" aria-label="Find anything">
        <div className={s.inputRow}>
          <Search size={17} aria-hidden="true" style={{ color: 'var(--ink-mute)' }} />
          <input
            ref={inputRef}
            className={s.input}
            value={q}
            onChange={(e) => { setQ(e.target.value); setCursor(0); }}
            onKeyDown={onKeyDown}
            placeholder="Plate, driver, thing name, campaign, or a place to go…"
            aria-label="Search"
          />
          <span className={s.esc}>ESC</span>
        </div>

        <div className={s.results}>
          {all.length === 0 && (
            <div className={s.hint}>{searching ? 'Searching…' : q.trim().length < 2 ? 'Type to search.' : 'Nothing found.'}</div>
          )}
          {all.map((item, i) => {
            const head = item.kind !== lastKind ? item.kind : null;
            lastKind = item.kind;
            return (
              <React.Fragment key={item.id}>
                {head && <div className={s.groupLabel}>{head === 'Go' ? 'Go to' : head}</div>}
                <button
                  type="button"
                  className={`${s.row} ${i === cursor ? s.rowOn : ''}`}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(item)}
                >
                  <span className={s.rowMain}>
                    <span className={s.rowTitle}>{item.title}</span>
                    {item.sub && <span className={s.rowSub} style={{ display: 'block' }}>{item.sub}</span>}
                  </span>
                  {item.external && <span className={s.rowKind}>classic</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className={s.foot}>
          <span>↑↓ move</span><span>⏎ open</span><span>esc close</span>
          {searching && <span style={{ marginLeft: 'auto' }}>searching…</span>}
        </div>
      </div>
    </div>
  );
}
